// Batch "parse all files" mode — the feedback-collection deliverable.
//
// Opens an overlay panel that iterates every file in the current FolderSource
// through kolilang.parse, collects per-file diagnostics into a sortable table,
// and exposes a JSON export suitable for attaching to a GitHub issue.
//
// Iteration is chunked through requestIdleCallback so the UI stays responsive
// on large corpora.

import type { FolderSource, FileEntry } from './fs';
import type { Kolilang, Diagnostic } from './wasm';

export interface BatchRow {
  path: string;
  tokens: number;
  stmts: number;
  diagCount: number;
  /** PROC invocations parsed from this file: lowercase name → count. */
  procs: Record<string, number>;
  firstDiagnostic: Diagnostic | null;
  /** null if read/parse succeeded; error message otherwise */
  error: string | null;
}

/** Corpus-wide PROC ranking row. */
export interface ProcFrequencyEntry {
  proc: string;
  /** Total proc_call statements across the corpus. */
  calls: number;
  /** Number of files containing at least one call. */
  files: number;
}

export interface BatchResult {
  schemaVersion: 2;
  kolilangVersion: string;
  timestamp: string;
  folderLabel: string;
  procFrequency: ProcFrequencyEntry[];
  files: Array<{
    path: string;
    tokens: number;
    stmts: number;
    procs: Record<string, number>;
    diagnostics: Diagnostic[];
    error?: string;
  }>;
}

type SortKey = 'path' | 'tokens' | 'stmts' | 'diags';
type SortDir = 'asc' | 'desc';

export async function runBatch(
  kolilang: Kolilang,
  source: FolderSource,
  onOpenFile: (path: string, diagnostic: Diagnostic | null) => void,
): Promise<void> {
  const overlay = buildOverlay();
  document.body.appendChild(overlay.root);

  // Load file list up-front.
  let entries: FileEntry[];
  try {
    entries = (await source.list()).filter((e) => e.kind === 'code');
  } catch (e) {
    overlay.status.textContent = `failed to list folder: ${e instanceof Error ? e.message : String(e)}`;
    return;
  }
  if (entries.length === 0) {
    overlay.status.textContent = 'no .sas files found';
    return;
  }

  overlay.status.textContent = `parsing 0 / ${entries.length}...`;

  const rows: BatchRow[] = [];
  const fullDiagnostics: Diagnostic[][] = []; // parallel to rows
  const t0 = performance.now();

  // Chunked iteration via requestIdleCallback, so the UI keeps updating the
  // progress counter. Falls back to setTimeout for environments without rIC.
  const rICOrTimeout: typeof requestIdleCallback =
    (window as any).requestIdleCallback ?? ((cb: any) => setTimeout(() => cb({ timeRemaining: () => 5 }), 0));

  let i = 0;
  await new Promise<void>((resolve) => {
    const step = async () => {
      const chunkEnd = Math.min(i + 4, entries.length);
      for (; i < chunkEnd; i += 1) {
        const e = entries[i];
        try {
          const text = await source.read(e.path);
          // Parse only — the result envelope carries the token total so
          // we skip a separate lex() round-trip (halves WASM calls per file).
          const parse = kolilang.parse(text);
          // Token-derived counts (parse.procs) rather than stmt-derived:
          // stmts come from the macro-expanded program, which misses
          // procs inside never-invoked %macro definitions — i.e. macro
          // libraries, exactly where TLF code keeps its reporting procs.
          const procs: Record<string, number> = {};
          for (const p of parse.procs) {
            procs[p.name] = (procs[p.name] ?? 0) + p.count;
          }
          rows.push({
            path: e.path,
            tokens: parse.tokens,
            stmts: parse.stmtCount,
            diagCount: parse.diagCount,
            procs,
            firstDiagnostic: parse.diagnostics[0] ?? null,
            error: null,
          });
          fullDiagnostics.push(parse.diagnostics);
        } catch (err) {
          rows.push({
            path: e.path,
            tokens: 0,
            stmts: 0,
            diagCount: 1,
            procs: {},
            firstDiagnostic: null,
            error: err instanceof Error ? err.message : String(err),
          });
          fullDiagnostics.push([]);
        }
      }
      overlay.status.textContent = `parsing ${i} / ${entries.length}...`;
      if (i < entries.length) {
        rICOrTimeout(step as unknown as IdleRequestCallback);
      } else {
        resolve();
      }
    };
    rICOrTimeout(step as unknown as IdleRequestCallback);
  });

  const elapsed = performance.now() - t0;
  const clean = rows.filter((r) => r.diagCount === 0 && r.error === null).length;
  const totalDiags = rows.reduce((acc, r) => acc + r.diagCount, 0);
  overlay.status.textContent =
    `${rows.length} files · ${clean} clean · ${rows.length - clean} with diagnostics · ` +
    `${totalDiags} total diagnostics · parsed in ${(elapsed / 1000).toFixed(2)}s`;

  // Corpus-wide ranking: total calls + how many files use each proc.
  const procAgg: ProcFrequencyEntry[] = (() => {
    const acc = new Map<string, { calls: number; files: number }>();
    for (const r of rows) {
      for (const [name, n] of Object.entries(r.procs)) {
        const cur = acc.get(name) ?? { calls: 0, files: 0 };
        cur.calls += n;
        cur.files += 1;
        acc.set(name, cur);
      }
    }
    return [...acc.entries()]
      .map(([proc, v]) => ({
        proc,
        calls: v.calls,
        files: v.files,
      }))
      .sort((a, b) => b.calls - a.calls || a.proc.localeCompare(b.proc));
  })();

  // Sorting state.
  let sortKey: SortKey = 'diags';
  let sortDir: SortDir = 'desc';
  let filter = '';
  /** When set, the file table shows only files that call this proc. */
  let procFilter: string | null = null;

  function redrawProcs(): void {
    overlay.procSection.style.display = procAgg.length ? '' : 'none';
    const tbody = overlay.procTbody;
    tbody.innerHTML = '';
    for (const p of procAgg) {
      const tr = document.createElement('tr');
      if (procFilter === p.proc) tr.classList.add('proc-selected');
      tr.title = 'click to filter the file list to files calling this proc';
      for (const text of [p.proc, String(p.calls), String(p.files)]) {
        const td = document.createElement('td');
        td.textContent = text;
        tr.appendChild(td);
      }
      tr.addEventListener('click', () => {
        procFilter = procFilter === p.proc ? null : p.proc;
        redrawProcs();
        redraw();
      });
      tbody.appendChild(tr);
    }
  }

  overlay.filterInput.addEventListener('input', () => {
    filter = overlay.filterInput.value.trim().toLowerCase();
    redraw();
  });

  for (const key of ['path', 'tokens', 'stmts', 'diags'] as const) {
    const th = overlay.headers[key];
    th.addEventListener('click', () => {
      if (sortKey === key) sortDir = sortDir === 'asc' ? 'desc' : 'asc';
      else {
        sortKey = key;
        sortDir = key === 'path' ? 'asc' : 'desc';
      }
      redraw();
    });
  }

  overlay.exportBtn.addEventListener('click', () => {
    const out: BatchResult = {
      schemaVersion: 2,
      kolilangVersion: kolilang.version(),
      timestamp: new Date().toISOString(),
      folderLabel: source.label,
      procFrequency: procAgg,
      files: rows.map((r, idx) => ({
        path: r.path,
        tokens: r.tokens,
        stmts: r.stmts,
        procs: r.procs,
        diagnostics: fullDiagnostics[idx],
        ...(r.error ? { error: r.error } : {}),
      })),
    };
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kolilang-batch-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  });

  overlay.closeBtn.addEventListener('click', () => overlay.root.remove());
  document.addEventListener('keydown', function onKey(ev) {
    if (ev.key === 'Escape') {
      overlay.root.remove();
      document.removeEventListener('keydown', onKey);
    }
  });

  function redraw(): void {
    const pathFiltered = filter
      ? rows.filter((r) => r.path.toLowerCase().includes(filter))
      : rows;
    const filtered = procFilter
      ? pathFiltered.filter((r) => r.procs[procFilter as string] !== undefined)
      : pathFiltered;
    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'path':   cmp = a.path.localeCompare(b.path); break;
        case 'tokens': cmp = a.tokens - b.tokens; break;
        case 'stmts':  cmp = a.stmts  - b.stmts; break;
        case 'diags':  cmp = a.diagCount - b.diagCount; break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    for (const key of ['path', 'tokens', 'stmts', 'diags'] as const) {
      const th = overlay.headers[key];
      th.dataset.dir = sortKey === key ? sortDir : '';
    }

    const tbody = overlay.tbody;
    tbody.innerHTML = '';
    for (const row of sorted) {
      const tr = document.createElement('tr');
      tr.className = row.error ? 'row-err' : row.diagCount === 0 ? 'row-ok' : 'row-warn';

      const td = (text: string, cls?: string) => {
        const el = document.createElement('td');
        el.textContent = text;
        if (cls) el.className = cls;
        return el;
      };

      tr.appendChild(td(row.path, 'col-path'));
      tr.appendChild(td(String(row.tokens), 'col-n'));
      tr.appendChild(td(String(row.stmts), 'col-n'));
      tr.appendChild(td(String(row.diagCount), 'col-n'));
      tr.appendChild(
        td(
          row.error ?? (row.firstDiagnostic ? row.firstDiagnostic.msg : ''),
          'col-first',
        ),
      );

      tr.addEventListener('click', () => {
        overlay.root.remove();
        onOpenFile(row.path, row.firstDiagnostic);
      });
      tbody.appendChild(tr);
    }
  }

  redrawProcs();
  redraw();
}

interface Overlay {
  root: HTMLElement;
  status: HTMLElement;
  procSection: HTMLElement;
  procTbody: HTMLElement;
  filterInput: HTMLInputElement;
  headers: Record<SortKey, HTMLElement>;
  tbody: HTMLElement;
  exportBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
}

function buildOverlay(): Overlay {
  const root = document.createElement('div');
  root.className = 'batch-overlay';

  const panel = document.createElement('div');
  panel.className = 'batch-panel';
  root.appendChild(panel);

  const header = document.createElement('div');
  header.className = 'batch-header';
  const title = document.createElement('div');
  title.className = 'batch-title';
  title.textContent = 'parse all files';
  const closeBtn = document.createElement('button');
  closeBtn.className = 'batch-close';
  closeBtn.textContent = '✕';
  header.appendChild(title);
  header.appendChild(closeBtn);
  panel.appendChild(header);

  const status = document.createElement('div');
  status.className = 'batch-status';
  panel.appendChild(status);

  // PROC frequency summary — collapsible so the diagnostics workflow
  // stays primary; hidden entirely until the scan finds proc calls.
  const procSection = document.createElement('details');
  procSection.className = 'batch-proc-summary';
  procSection.open = true;
  procSection.style.display = 'none';
  const procLabel = document.createElement('summary');
  procLabel.textContent = 'proc frequency';
  procSection.appendChild(procLabel);
  const procTable = document.createElement('table');
  procTable.className = 'batch-table';
  const procThead = document.createElement('thead');
  const procHrow = document.createElement('tr');
  for (const label of ['proc', 'calls', 'files']) {
    const th = document.createElement('th');
    th.textContent = label;
    procHrow.appendChild(th);
  }
  procThead.appendChild(procHrow);
  procTable.appendChild(procThead);
  const procTbody = document.createElement('tbody');
  procTable.appendChild(procTbody);
  procSection.appendChild(procTable);
  panel.appendChild(procSection);

  const controls = document.createElement('div');
  controls.className = 'batch-controls';
  const filterInput = document.createElement('input');
  filterInput.placeholder = 'filter by path...';
  filterInput.className = 'batch-filter';
  const exportBtn = document.createElement('button');
  exportBtn.className = 'batch-export';
  exportBtn.textContent = 'export JSON';
  controls.appendChild(filterInput);
  controls.appendChild(exportBtn);
  panel.appendChild(controls);

  const table = document.createElement('table');
  table.className = 'batch-table';
  const thead = document.createElement('thead');
  const hrow = document.createElement('tr');
  const headers: Record<SortKey, HTMLElement> = {} as any;
  const defs: Array<[SortKey, string, string]> = [
    ['path', 'path', 'col-path'],
    ['tokens', 'tokens', 'col-n'],
    ['stmts', 'stmts', 'col-n'],
    ['diags', 'diags', 'col-n'],
  ];
  for (const [key, label, cls] of defs) {
    const th = document.createElement('th');
    th.textContent = label;
    th.className = cls;
    th.dataset.sortKey = key;
    hrow.appendChild(th);
    headers[key] = th;
  }
  const thFirst = document.createElement('th');
  thFirst.textContent = 'first diagnostic';
  thFirst.className = 'col-first';
  hrow.appendChild(thFirst);
  thead.appendChild(hrow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  table.appendChild(tbody);
  panel.appendChild(table);

  return { root, status, procSection, procTbody, filterInput, headers, tbody, exportBtn, closeBtn };
}
